import React, { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { cn } from "./ui/utils";
import {
  formatMinutes,
  formatNumber,
  formatPct,
  formatRupees,
  sortRows,
} from "./d2cFormat";

const COLUMNS = [
  { key: "name", label: "Dealer" },
  { key: "franchise_code", label: "Code" },
  { key: "city", label: "City" },
  { key: "offers", label: "Offers", numeric: true },
  { key: "accepted", label: "Accepted", numeric: true },
  { key: "declined", label: "Declined", numeric: true },
  { key: "no_response", label: "No response", numeric: true },
  { key: "taken_by_other", label: "Taken by other", numeric: true },
  { key: "delivered", label: "Delivered", numeric: true },
  { key: "released", label: "Released", numeric: true },
  { key: "breaches", label: "Breaches", numeric: true, alertAbove: 0 },
  { key: "fees_amount", label: "Fees", numeric: true, format: formatRupees },
  { key: "acceptance_rate", label: "Acceptance", numeric: true, format: formatPct },
  { key: "median_minutes_to_accept", label: "Median to accept", numeric: true, format: formatMinutes },
];

const DealerTable = ({ dealers }) => {
  const [sort, setSort] = useState({ key: "offers", direction: "desc" });
  const rows = useMemo(() => sortRows(dealers, sort.key, sort.direction), [dealers, sort]);

  const toggleSort = (key) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: COLUMNS.find((c) => c.key === key)?.numeric ? "desc" : "asc" },
    );
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {COLUMNS.map((column) => {
            const active = sort.key === column.key;
            const Icon = active ? (sort.direction === "asc" ? ArrowUp : ArrowDown) : ChevronsUpDown;
            return (
              <TableHead
                key={column.key}
                className={column.numeric ? "tw-text-right" : ""}
                aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
              >
                <button
                  type="button"
                  onClick={() => toggleSort(column.key)}
                  className={cn(
                    "tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-rounded tw-bg-transparent tw-p-0 tw-font-sans tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring",
                    active ? "tw-text-foreground" : "tw-text-muted-foreground",
                  )}
                >
                  {column.label}
                  <Icon className={cn("tw-size-3", active ? "tw-opacity-100" : "tw-opacity-50")} aria-hidden="true" />
                </button>
              </TableHead>
            );
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((dealer) => (
          <TableRow key={dealer.store_id || dealer.franchise_code} className="hover:tw-bg-muted/40">
            {COLUMNS.map((column) => {
              const value = dealer[column.key];
              const format = column.format || (column.numeric ? formatNumber : (v) => v || "-");
              const alert = column.alertAbove !== undefined && Number(value) > column.alertAbove;
              return (
                <TableCell
                  key={column.key}
                  className={cn(
                    column.numeric && "tw-text-right tw-tabular-nums",
                    column.key === "name" && "tw-font-medium",
                    column.key === "franchise_code" && "tw-font-mono tw-text-xs",
                    alert && "tw-font-medium tw-text-red-600",
                  )}
                >
                  {format(value)}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default DealerTable;
